import { useCallback, useEffect, useRef, useState, Fragment } from "react"
import useAnimateIn from "../../hooks/useAnimateIn"
import API from "../../api"
import "./cohort-analysis.page.css"
import { mean, standardDeviation } from "simple-statistics"
import { max, scaleLinear } from "d3"
import {
  CartesianGrid,
  ResponsiveContainer,
  Scatter,
  XAxis,
  YAxis,
  Line,
  ComposedChart,
  Label,
  Legend,
  RadarChart,
  PolarGrid,
  PolarRadiusAxis,
  PolarAngleAxis,
  Radar,
  BarChart,
  Bar,
  Cell,
  ErrorBar,
} from "recharts"
import { Polyfit } from "../../utils/polyfit"
import { graphIcon, plusIcon, xIcon } from "../../icons"
import useClickOutside from "../../hooks/useClickOutside"
import SpinnerComponent from "../../components/spinner/spinner.component"
import DropdownComponent from "../../components/dropdown/dropdown.component"

const CohortAnalysisPage = (props) => {
  const { user } = props
  const [loading, setLoading] = useState(true)
  const [showTrials, setShowTrials] = useState()
  const [availableTrials, setAvailableTrials] = useState([])
  const [selectedTrials, setSelectedTrials] = useState([])
  const [radarData, setRadarData] = useState([])
  const [chartData, setChartData] = useState()
  const [barData, setBarData] = useState([])
  const menuRef = useRef()
  const timerRef = useRef()
  useClickOutside(menuRef, () => setShowTrials(false))

  useAnimateIn()

  const createChartLine = useCallback((data, xMetric, yMetric) => {
    const dataSubset = [...data].map((i) => {
      return { x: parseFloat(i[xMetric]), y: parseFloat(i[yMetric]) }
    })

    const xVals = dataSubset.map((d) => d.x)
    const yVals = dataSubset.map((d) => d.y)

    // Create polyfit line
    const poly = new Polyfit(xVals, yVals)
    const solver = poly.getPolynomial(2)
    const xMin = Math.min(...xVals)
    const xMax = Math.max(...xVals)
    const xValsLinSpaceGen = scaleLinear([xMin, xMax], [0, 100])
    const xValsLinSpace = xValsLinSpaceGen.ticks(100)
    const lineVals = []
    xValsLinSpace.forEach((x) => {
      const val = solver(x)
      lineVals.push({ x, y: val })
    })

    return lineVals
  }, [])

  // Fetch on load
  useEffect(() => {
    const fetchTests = async () => {
      try {
        let trials = await API.makeGetRequest(user, `trials`)
        const colors = ["#6FD195", "#FFAE4C", "#7086FD"]
        let colorIndex = 0

        trials = trials.map((t) => {
          const color = colors[colorIndex]
          if (colorIndex + 1 > colors.length - 1) {
            colorIndex = 0
          } else {
            colorIndex++
          }

          return { ...t, color }
        })

        setAvailableTrials(trials)
      } catch {
        // todo handle error
      } finally {
        setLoading(false)
      }
    }

    if (user) {
      fetchTests()
    }
  }, [user])

  const handleSelectTrial = (t) => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
    }

    let dest
    if (selectedTrials.findIndex((st) => st.id === t.id) > -1) {
      dest = [...selectedTrials].filter((st) => st.id !== t.id)
    } else {
      dest = [...selectedTrials, t]
    }

    setSelectedTrials(dest)

    // Make api call
    timerRef.current = setTimeout(() => handleFetchTests(dest), 500)
  }

  // const handleDone = () => {
  //   setShowTrials(false)
  //   handleFetchTests(selectedTrials)
  // }

  const handleFetchTests = async (trials) => {
    if (trials.length < 1) return

    try {
      setLoading(true)
      setChartData([])
      const { tests, testsData } = await API.makePostRequest(user, `trials`, {
        trials: trials.map((t) => t.id),
      })

      let buckets = trials.map((t) => {
        return { id: t.id, name: t.name, color: t.color, data: [], line: [] }
      })

      tests.forEach((t) => {
        const bucketIndex = buckets.findIndex((b) => b.id === t.trialId)
        if (bucketIndex > -1) {
          buckets[bucketIndex].data.push(t)
        }
      })

      console.log("create main graph")
      const radarMap = {}
      const barMap = {}
      buckets = buckets.map((b) => {
        const radar = createRadarChart(testsData, b.id)
        const bars = createBarCharts(testsData, b.id)
        radarMap[b.id] = radar
        barMap[b.id] = bars
        return {
          ...b,
          line: createChartLine(b.data, "days", "persistence"),
        }
      })

      console.log("create radar")
      const radar = [
        {
          subject: "Activation",
        },
        {
          subject: "CAR",
        },
        {
          subject: "Cytotoxicity",
        },
      ]

      const bars = [
        {
          subject: "Activation",
          entries: [],
        },
        {
          subject: "CAR",
          entries: [],
        },
        {
          subject: "Cytotoxicity",
          entries: [],
        },
      ]

      Object.keys(radarMap).forEach((k) => {
        radar[0][k] = radarMap[k].activation
        radar[1][k] = radarMap[k].car
        radar[2][k] = radarMap[k].cyto
      })

      Object.keys(barMap).forEach((k) => {
        const color = buckets.find((b) => b.id === k).color
        bars[0].entries.push({
          label: k,
          value: barMap[k].activation,
          err: barMap[k].activationErr,
          color,
        })
        bars[1].entries.push({
          label: k,
          value: barMap[k].car,
          err: barMap[k].carErr,
          color,
        })
        bars[2].entries.push({
          label: k,
          value: barMap[k].cyto,
          err: barMap[k].cytoErr,
          color,
        })
      })

      console.log(buckets, radar, bars)

      setChartData(buckets)
      setRadarData(radar)
      setBarData(bars)
    } catch {
      // todo handle error
    } finally {
      setLoading(false)
    }
  }

  const createRadarChart = (data, trial) => {
    // Get mean of data
    const filtered = data.filter((d) => d.trialId === trial)

    if (filtered.length < 1) {
      return {}
    }

    const maxes = {
      activation: max(data.map((d) => parseFloat(d.activationNkg2d))),
      car: max(data.map((d) => parseFloat(d.car))),
      cyto: max(data.map((d) => parseFloat(d.cytotoxicityCd107a))),
      persistence: max(data.map((d) => parseFloat(d.persistence))),
    }

    let radar = {
      activation:
        mean(filtered.map((d) => parseFloat(d.activationNkg2d))) /
        maxes.activation,
      car: mean(filtered.map((d) => parseFloat(d.car))) / maxes.car,
      cyto:
        mean(filtered.map((d) => parseFloat(d.cytotoxicityCd107a))) /
        maxes.cyto,
      persistence:
        mean(filtered.map((d) => parseFloat(d.persistence))) /
        maxes.persistence,
    }

    radar = {
      activation: radar.activation * radar.persistence,
      car: radar.car * radar.persistence,
      cyto: radar.cyto * radar.persistence,
    }

    return radar
  }

  const createBarCharts = (allData, trialId) => {
    const data = allData.filter((d) => d.trialId === trialId)

    if (data.length < 1) {
      return {}
    }

    const act = data.map((d) => parseFloat(d.activationNkg2d))
    const car = data.map((d) => parseFloat(d.car))
    const cyto = data.map((d) => parseFloat(d.cytotoxicityCd107a))

    const bar = {
      activation: mean(act),
      activationErr: standardDeviation(act),
      car: mean(car),
      carErr: standardDeviation(car),
      cyto: mean(cyto),
      cytoErr: standardDeviation(cyto),
    }

    return bar
  }

  return (
    <div className="cb-page">
      <div className="cb-page-header">
        <input type="text" placeholder="Search..." />
      </div>
      <div className="cb-page-content cb-cohort">
        <div className="cb-cohort-header">
          {graphIcon}
          <div>
            <h3>Cohort Analysis</h3>
            <h5>Here's your Cohort details</h5>
          </div>
        </div>
        <div className="cb-cohort-filters">
          <div className="cb-trial-list">
            {availableTrials.map((a) => {
              const selected =
                selectedTrials.findIndex((st) => st.id === a.id) > -1
              const color = selected ? "#fff" : a.color
              const backgroundColor = selected ? a.color : "transparent"
              const borderColor = a.color
              return (
                <button
                  style={{ color, backgroundColor, borderColor }}
                  onClick={() => handleSelectTrial(a)}
                  key={a.id}
                >
                  {a.id}
                  {selected && xIcon}
                </button>
              )
            })}
            {/* {selectedTrials.length < 1 && (
              <div className="cb-placeholder">No trials selected...</div>
            )} */}
            {/* {selectedTrials.map((t) => {
              return (
                <button
                  className="cb-clear-btn"
                  key={t.id}
                  onClick={() => handleSelectTrial(t)}
                >
                  {t.name} {xIcon}
                </button>
              )
            })}
            <button
              className="cb-add-btn"
              onClick={() => setShowTrials(!showTrials)}
            >
              {plusIcon}
            </button> */}
            {/* {showTrials && (
              <div className="cb-trials" ref={menuRef}>
                {availableTrials.map((a) => {
                  const selected =
                    selectedTrials.findIndex((st) => st.id === a.id) > -1
                  return (
                    <button
                      className={selected ? "cb-clear-btn" : ""}
                      onClick={() => handleSelectTrial(a, true)}
                      key={a.id}
                    >
                      {a.name}
                      {selected && xIcon}
                    </button>
                  )
                })}
                <button className="cb-done-btn" onClick={handleDone}>
                  Done
                </button>
              </div>
            )} */}
          </div>
          <DropdownComponent placeholder="All Patients" disabled />
          <DropdownComponent placeholder="All Tests" disabled />
        </div>
        {loading && <SpinnerComponent />}
        {chartData && selectedTrials.length > 0 && !showTrials && !loading && (
          <>
            <div className="cb-cohort-chart">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                  margin={{
                    right: 20,
                    left: 20,
                    top: 20,
                    bottom: 20,
                  }}
                >
                  <CartesianGrid />

                  {chartData.map((c) => {
                    return (
                      <Fragment key={c.id}>
                        <Scatter
                          name={c.name}
                          xAxisId={1}
                          data={c.data}
                          fill={c.color}
                        />
                        <XAxis type="number" dataKey="x" xAxisId={c.id} hide />
                        <Line
                          xAxisId={c.id}
                          dataKey="y"
                          data={c.line}
                          stroke={c.color}
                          strokeWidth={2}
                          dot={false}
                          legendType="none"
                        />
                      </Fragment>
                    )
                  })}
                  <XAxis
                    domain={[0, 120]}
                    type="number"
                    dataKey="days"
                    xAxisId={1}
                    label={
                      <Label
                        value="Days Since Peak Expansion"
                        position={"insideBottom"}
                        offset={-10}
                      />
                    }
                  />
                  <YAxis
                    type="number"
                    dataKey="persistence"
                    label={
                      <Label
                        angle={-90}
                        value="Persistence"
                        position="insideLeft"
                        style={{ textAnchor: "middle" }}
                      />
                    }
                  />
                  <Legend verticalAlign="top" height={36} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            <div className="cb-cohort-charts">
              <div className="cb-cohort-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart
                    cx="50%"
                    cy="50%"
                    outerRadius="80%"
                    data={radarData}
                    margin={{ left: 20, right: 20 }}
                  >
                    <PolarGrid />
                    <PolarAngleAxis dataKey="subject" />
                    <PolarRadiusAxis />
                    {chartData.map((c) => {
                      return (
                        <Fragment key={c.id}>
                          <Radar
                            name={c.id}
                            dataKey={c.id}
                            fill={c.color}
                            strokeWidth={3}
                            stroke={c.color}
                            fillOpacity={0.6}
                          />
                        </Fragment>
                      )
                    })}
                  </RadarChart>
                </ResponsiveContainer>
              </div>
              <div className="cb-cohort-chart">
                <h5>Activation</h5>
                <ResponsiveContainer width="100%" height="80%">
                  <BarChart
                    data={barData[0] && barData[0].entries}
                    margin={{
                      top: 10,
                      right: 10,
                      left: 10,
                      bottom: 10,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="label" />
                    <YAxis />
                    <Bar dataKey="value">
                      {barData[0] &&
                        barData[0].entries.map((entry, i) => (
                          <Fragment key={i}>
                            <Cell key={entry.label} fill={entry.color} />
                            <ErrorBar
                              key={`ac-${i}`}
                              dataKey="err"
                              width={12}
                              strokeWidth={1}
                              stroke="#111"
                              direction="y"
                            />
                          </Fragment>
                        ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="cb-cohort-chart">
                <h5>CAR</h5>
                <ResponsiveContainer width="100%" height="80%">
                  <BarChart
                    data={barData[1] && barData[1].entries}
                    margin={{
                      top: 10,
                      right: 10,
                      left: 10,
                      bottom: 10,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="label" />
                    <YAxis />
                    <Bar dataKey="value">
                      {barData[1] &&
                        barData[1].entries.map((entry, i) => (
                          <Fragment key={i}>
                            <Cell key={entry.label} fill={entry.color} />
                            <ErrorBar
                              key={`car-${i}`}
                              dataKey="err"
                              width={12}
                              strokeWidth={1}
                              stroke="#111"
                              direction="y"
                            />
                          </Fragment>
                        ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="cb-cohort-chart">
                <h5>Cytotoxicity</h5>
                <ResponsiveContainer width="100%" height="80%">
                  <BarChart
                    data={barData[2] && barData[2].entries}
                    margin={{
                      top: 10,
                      right: 10,
                      left: 10,
                      bottom: 10,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="label" />
                    <YAxis />
                    <Bar dataKey="value">
                      {barData[2] &&
                        barData[2].entries.map((entry, i) => (
                          <Fragment key={i}>
                            <Cell key={entry.label} fill={entry.color} />
                            <ErrorBar
                              key={`cyto-${i}`}
                              dataKey="err"
                              width={12}
                              strokeWidth={1}
                              stroke="#111"
                              direction="y"
                            />
                          </Fragment>
                        ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default CohortAnalysisPage
